<template>
  <FusionBaseDialog
    :open="open"
    :title="$t('map.deviceInfo.title')"
    icon="mdi-information"
    :persistentOverlay="true"
    @after-leave="$emit('afterLeave')"
    max-height="90%"
    padded
    data-cy="deviceInfoDialog"
  >
    <v-row>
      <v-col cols="12">
        <v-list>
          <v-list-item class="device-name">
            <!--when separate Device and Location names is enabled-->
            <v-list-item-title> {{ $t('map.deviceInfo.deviceName') }}: </v-list-item-title>
            <v-tooltip location="bottom" :max-width="600" :disabled="!isNameOverflowing">
              <template v-slot:activator="{ props }">
                <v-list-item-subtitle v-bind="props" ref="deviceName">
                  <span data-cy="deviceInfoName">{{ friendlyName }}</span>
                </v-list-item-subtitle>
              </template>
              <span>{{ friendlyName }}</span>
            </v-tooltip>
            <v-list-item-title style="margin-top: 5px">
              {{ $t('map.deviceInfo.locationName') }}:
            </v-list-item-title>
            <v-tooltip location="bottom" :max-width="600" :disabled="!isLocationNameOverflowing">
              <template v-slot:activator="{ props }">
                <v-list-item-subtitle v-bind="props" ref="deviceLocationName">
                  <span data-cy="deviceInfoLocation">{{ locationName }}</span>
                </v-list-item-subtitle>
              </template>
              <span>{{ locationName }}</span>
            </v-tooltip>
          </v-list-item>
        </v-list>
      </v-col>
      <v-col cols="12" sm="6">
        <v-list>
          <v-list-item data-cy="deviceInfoDevice">
            <v-list-item-title> {{ $t('map.deviceInfo.device') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ modelDisplayName }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item data-cy="deviceInfoModel">
            <v-list-item-title> {{ $t('map.deviceInfo.model') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ displayModel }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item data-cy="deviceInfoSubModel">
            <v-list-item-title> {{ $t('map.deviceInfo.subModel') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ submodel }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item data-cy="deviceInfoSerial">
            <v-list-item-title> {{ $t('map.deviceInfo.serial') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ serial }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item data-cy="deviceInfoMqttHost">
            <v-list-item-title> {{ $t('map.deviceInfo.mqttHost') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ mqttHost }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-col>
      <v-col cols="12" sm="6">
        <v-list>
          <v-list-item data-cy="deviceInfoLastCommunication">
            <v-list-item-title> {{ $t('map.deviceInfo.lastCommunication') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ getTimeSinceDate(lastCommunication) }}
            </v-list-item-subtitle>
          </v-list-item>
          <!-- Render with Wired Icon when Ethernet Connection-->
          <v-list-item v-if="isEthernet" data-cy="deviceInfoWiredConnection">
            <v-list-item-title>
              {{ $t('map.deviceInfo.connectionSignalStrength') }}:
            </v-list-item-title>
            <v-list-item-subtitle>
              <img :src="ethernetIcon" alt="Ethernet Icon" width="18" height="18" />
              {{ $t('map.deviceInfo.wiredConnection') }}
            </v-list-item-subtitle>
          </v-list-item>
          <!-- Render Wi-Fi info -->
          <v-list-item v-else data-cy="deviceInfoWifi">
            <v-list-item-title> {{ $t('map.deviceInfo.wifiStrength') }}: </v-list-item-title>
            <v-list-item-subtitle v-if="showConnectivity">
              {{ connectionStrengthText }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-else>
              {{ $t('map.deviceInfo.notApplicable') }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item data-cy="deviceInfoRssi">
            <v-list-item-title> {{ $t('map.deviceInfo.rssi') }}: </v-list-item-title>
            <v-list-item-subtitle v-if="isEthernet || !showConnectivity">
              {{ $t('map.deviceInfo.notApplicable') }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-else>
              {{ rssi }} {{ $t('map.deviceInfo.rssiUnits') }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item data-cy="deviceInfoFirmware">
            <v-list-item-title> {{ $t('map.deviceInfo.firmware') }}: </v-list-item-title>
            <v-list-item-subtitle>
              {{ firmware }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
    <DeviceSubscriptionRow :deviceId="deviceId" :showSubscriptionType="subscriptionTypes.WEBSITE" />
    <DeviceSubscriptionRow
      :deviceId="deviceId"
      :showSubscriptionType="subscriptionTypes.DATA_SERVICES"
    />
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        @click="close"
        class="bg-primary text-white"
        variant="text"
        data-cy="deviceInfoDoneButton"
      >
        {{ $t('buttons.done') }}
      </v-btn>
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue'

import { deviceModelDisplayLong, getRelativeTimeSince } from '@/services/map'
import { isChildOverflowing } from '@/services/html-utilities'
import { subscriptionTypes } from '@/store/subscriptions/utils'
import DeviceSubscriptionRow from './content/SubscriptionDetailsRow.vue'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'

const ethernet = 'ethernet'

export default defineComponent({
  name: 'DeviceInfoDialog',
  props: ['open', 'deviceId'],
  emits: ['close', 'afterLeave'],
  components: {
    DeviceSubscriptionRow
  },
  data() {
    return {
      isNameOverflowing: false,
      isLocationNameOverflowing: false,
      subscriptionTypes,
      ethernetIcon: '/img/icons/ethernet_icon.svg'
    }
  },
  mounted() {
    this.init()
  },
  computed: {
    showConnectivity(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_CONNECTIVITY}`
      ](this.model)
    },
    rssi(): number {
      const rssi = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_RSSI_BY_DEVICE_ID}`
      ](this.deviceId)
      return rssi || this.$t('map.popover.valueBlank')
    },
    isEthernet(): boolean {
      const connectionType = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTION_TYPE_BY_DEVICE_ID}`
      ](this.deviceId)
      return connectionType === ethernet
    },
    connectionStrengthText(): string {
      if (this.rssi) {
        if (this.rssi > -67) {
          return this.$t('map.popover.connectionStrength.wifi.veryGood')
        }
        if (this.rssi > -70) {
          return this.$t('map.popover.connectionStrength.wifi.good')
        }
        if (this.rssi > -80) {
          return this.$t('map.popover.connectionStrength.wifi.okay')
        }
        if (this.rssi > -90) {
          return this.$t('map.popover.connectionStrength.wifi.bad')
        }
        if (this.rssi <= -90) {
          return this.$t('map.popover.connectionStrength.wifi.unusable')
        }
      }
      return this.$t('map.popover.valueBlank')
    },
    lastCommunication(): Date | null {
      const lastCommunicationTimestamp = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_COMMUNICATION_TIME_BY_DEVICE_ID}`
      ](this.deviceId)
      if (!lastCommunicationTimestamp) {
        return null
      }
      return new Date(lastCommunicationTimestamp)
    },
    friendlyName(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    locationName(): string {
      const locationName = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LOCATION_NAME_BY_DEVICE_ID}`
      ](this.deviceId)
      return locationName || this.$t('map.popover.valueBlank')
    },
    model(): string {
      const model = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ](this.deviceId)
      return model
    },
    displayModel(): string {
      const modelName = this.model
      return modelName ? modelName.toUpperCase() : this.$t('map.popover.valueBlank')
    },
    submodel(): string {
      const sub = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
      ](this.deviceId)
      return sub || this.$t('map.deviceInfo.noSubModel')
    },
    serial(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    firmware(): string {
      const fw = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_FIRMWARE_BY_DEVICE_ID}`
      ](this.deviceId)
      return fw || this.$t('map.popover.valueBlank')
    },
    mqttHost(): string {
      const host = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MQTT_HOST_BY_DEVICE_ID}`
      ](this.deviceId)
      return host || this.$t('map.popover.valueBlank')
    },
    modelDisplayName(): string {
      const modelKey = this.model
        ? this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`](
            this.model
          )
        : ''
      const name = deviceModelDisplayLong(modelKey, this.$t.bind(this))
      return name || this.$t('map.popover.valueBlank')
    }
  },
  methods: {
    init() {
      if (this.open) {
        this.setIsNameOverflowing()
        this.setIsLocationNameOverflowing()
      }
    },
    close() {
      this.$emit('close')
    },
    getTimeSinceDate(date: Date | null) {
      return getRelativeTimeSince(date, this.$t.bind(this))
    },
    async setIsNameOverflowing() {
      await nextTick()
      this.isNameOverflowing = isChildOverflowing(this.$refs.deviceName as HTMLElement)
    },
    async setIsLocationNameOverflowing() {
      await nextTick()
      this.isLocationNameOverflowing = isChildOverflowing(
        this.$refs.deviceLocationName as HTMLElement
      )
    }
  },
  watch: {
    open() {
      this.init()
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.v-list) {
  margin-left: -16px;
  margin-right: -16px;
}

.v-list-item {
  .v-list-item__content {
    padding: 8px 0;
  }
  .v-list-item-title {
    font-size: 0.875rem;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
    .device-name & {
      font-size: 1.425rem;
    }
  }
  .v-list-item-subtitle {
    font-size: 0.875rem;
    font-weight: 400;
    color: black;
    opacity: 1;
  }
  &.device-name {
    .v-list-item-title,
    .v-list-item-subtitle {
      font-size: 1.125rem;
    }
    .v-list-item-subtitle {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      white-space: normal;
      overflow: hidden;
      text-overflow: ellipsis;
      opacity: 1;
    }
  }
}
</style>
